import { AvoidType, DataType, InverterData, ReadWrite, RegisterTable } from "../lib/inverter.model";
import { cleanString } from "../lib/modbus.utils";

export const DSerialNumberAndVersion: Partial<RegisterTable> = {
  SN: {
    address: 0x8020,
    name: 'Serial_Number0-20',
    dataType: DataType.ASCII,
    description: 'Serial number',
    access: ReadWrite.Read,
    quantity: 10,
  },
  SN_OLD: {
    address: 0x0445,
    name: 'Serial_Number0-14',
    dataType: DataType.ASCII,
    description: 'Serial number',
    access: ReadWrite.Read,
    quantity: 7,
  },
  MODBUS_PROTOCOL_VERSION: {
    address: 0x0044,
    name: 'MODBUS_PROTOCOL_VERSION',
    dataType: DataType.BCD16,
    description: 'Modbus Protocol Version',
    access: ReadWrite.Read,
    quantity: 1,
  },
  ARM: {
    address: 0x044F,
    name: 'arm',
    dataType: DataType.ASCII,
    description: 'Communication CPU Software Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  HARDWARE_VERSION: {
    address: 0x044D,
    name: 'hardware_version',
    dataType: DataType.ASCII,
    description: 'Hardware Version',
    access: ReadWrite.Read,
    quantity: 2,
  },
  DSPM: {
    address: 0x044F + 0x0004,
    name: 'dspm',
    dataType: DataType.ASCII,
    description: 'Master Software Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  DSPS: {
    address: 0x044F + 0x0008,
    name: 'dsps',
    dataType: DataType.ASCII,
    description: 'Vice Software Version',
    access: ReadWrite.Read,
    quantity: 4,
    avoid: AvoidType.SPLIT_PAGE,
  },
  MAIN_VERSION: {
    address: 0x045B,
    name: 'main_safety_version',
    dataType: DataType.ASCII,
    description: 'Main Safety Version',
    access: ReadWrite.Read,
    quantity: 2,
  },
  SAFETY_FIRMWARE_VERSION: {
    address: 0x0460,
    name: 'safety_firmware_version',
    dataType: DataType.ASCII,
    description: 'Safety Firmware Version',
    access: ReadWrite.Read,
    quantity: 4,
  },
  SAFETY_VERSION: {
    address: 0x2005,
    name: 'SAFETY_VERSION',
    dataType: DataType.U16,
    description: 'Safety Version',
    access: ReadWrite.Read,
    quantity: 1,
  },
};

export enum V4ProtocolChanelType {
  PROTOCOL_CHANNEL_G3 = 0,
  PROTOCOL_CHANNEL_G4 = 1,
}

export const V4ProtocolChannels: Partial<RegisterTable> = {
  BLE_PROTOCOL_CHANNEL: {
    address: 0x10C4,
    name: 'BLE_PROTOCOL_CHANNEL',
    dataType: DataType.U16,
    description: 'BLE Protocol Channel',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  PLC_PROTOCOL_CHANNEL: {
    address: 0x10C5,
    name: 'PLC_PROTOCOL_CHANNEL',
    dataType: DataType.U16,
    description: 'PLC Protocol Channel',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  USB_PROTOCOL_CHANNEL: {
    address: 0x10C6,
    name: 'USB_PROTOCOL_CHANNEL',
    dataType: DataType.U16,
    description: 'USB Protocol Channel',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  RS485_PROTOCOL_CHANNEL_1: {
    address: 0x10C7,
    name: 'RS485_PROTOCOL_CHANNEL_1',
    dataType: DataType.U16,
    description: 'RS485 Protocol Channel 1',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
  RS485_PROTOCOL_CHANNEL_2: {
    address: 0x10C8,
    name: 'RS485_PROTOCOL_CHANNEL_2',
    dataType: DataType.U16,
    description: 'RS485 Protocol Channel 2',
    access: ReadWrite.ReadWrite,
    quantity: 1,
  },
};

export const V4CurrentProtocolChannel: Partial<RegisterTable> = {
  CURRENT_PROTOCOL_CHANNEL: {
    address: 0x4E20,
    name: 'CURRENT_PROTOCOL_CHANNEL',
    dataType: DataType.U16,
    description: 'Current Protocol Channel',
    access: ReadWrite.Read,
    quantity: 1,
  },
};

export const V3SerialNumberAndVersion: Partial<RegisterTable> = DSerialNumberAndVersion;

export const ABCSerialNumberAndVersion: Partial<RegisterTable> = {
  SnAndVersions: {
    address: 0x2000,
    name: 'SnAndVersions',
    dataType: DataType.ASCII,
    description: 'Serial number and versions',
    access: ReadWrite.Read,
    quantity: 16,
  },
};

export const ABCSerialNumberAndVersionComposer = {
  SN: (data: Partial<InverterData>) => {
    const value = cleanString(data.SnAndVersions?.toString().trim());
    if (value === undefined || typeof value !== 'string') {
      data.SN = null;
    }

    data.SN = value?.substring(0, 14);
    return data;
  },
  ARM: (data: Partial<InverterData>) => {
    const value = cleanString(data.SnAndVersions?.toString().trim());
    if (value === undefined || typeof value !== 'string') {
      data.ARM = null;
    }

    data.ARM = value?.substring(14, 18);
    return data;
  },
  HARDWARE_VERSION: (data: Partial<InverterData>) => {
    const value = cleanString(data.SnAndVersions?.toString().trim());
    if (value === undefined || typeof value !== 'string') {
      data.HARDWARE_VERSION = null;
    }

    data.HARDWARE_VERSION = value?.substring(18, 22);
    return data;
  },
  DSPS: (data: Partial<InverterData>) => {
    const value = cleanString(data.SnAndVersions?.toString().trim());
    if (value === undefined || typeof value !== 'string') {
      data.DSPS = null;
    }

    data.DSPS = value?.substring(22, 26);
    return data;
  },
  DSPM: (data: Partial<InverterData>) => {
    const value = cleanString(data.SnAndVersions?.toString().trim());
    if (value === undefined || typeof value !== 'string') {
      data.DSPM = null;
    }

    data.DSPM = value?.substring(26, 30);
    return data;
  },
};

export const DSerialNumberAndVersionComposer = {
  SN: (data: Partial<InverterData>) => {
    const value = cleanString(data.SN?.toString().trim()) || cleanString(data.SN_OLD?.toString().trim());
    if (value === undefined || typeof value !== 'string') {
      data.SN = null;
    }

    data.SN = value;
    return data;
  },
  ARM: (data: Partial<InverterData>) => {
    const value = cleanString(data.ARM?.toString().trim());
    if (value === undefined || typeof value !== 'string') {
      data.ARM = null;
    }

    data.ARM = value;
    return data;
  },
  DSPS: (data: Partial<InverterData>) => {
    const value = cleanString(data.DSPS?.toString().trim());
    if (value === undefined || typeof value !== 'string') {
      data.DSPS = null;
    }

    data.DSPS = value;
    return data;
  },
  DSPM: (data: Partial<InverterData>) => {
    const value = cleanString(data.DSPM?.toString().trim());
    if (value === undefined || typeof value !== 'string') {
      data.DSPM = null;
    }

    data.DSPM = value;
    return data;
  },
};
